/* eslint-disable max-lines */
import { computed } from '@vue/composition-api'
import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { can } from '@/ability'
import { StreamSubView } from '@/modules/dashboard/types'
import { Feature, hasFeature } from '@/featureFlags/featureFlags'

export enum LeftMenuType {
  GENERAL = 'general',
  SUB_MENU = 'subMenu',
  OPS = 'ops'
}

export interface LeftMenuLabel {
  label: string
  pathName: string
  type: LeftMenuType.GENERAL|LeftMenuType.OPS
  allow: AbilitySubject
  icon: string
  subView?: string
  children?: LeftMenuSubLabel[]
  hasBreak?: boolean
}

export interface LeftMenuSubLabel {
  label: string
  pathName: string
  type: LeftMenuType.SUB_MENU
  subView: string
  allow?: AbilitySubject
  icon?: string
  children?: LeftMenuSubLabel[]
}

const DASHBOARD_ITEMS: LeftMenuLabel[] = [
  {
    label: 'Productions',
    pathName: 'events',
    subView: 'active',
    allow: AbilitySubject.Event,
    type: LeftMenuType.GENERAL,
    icon: 'events',
    hasBreak: false,
    children: [
      { label: 'Active', pathName: 'events', subView: 'active', type: LeftMenuType.SUB_MENU },
      { label: 'Scheduled', pathName: 'events', subView: 'scheduled', type: LeftMenuType.SUB_MENU }
    ]
  }, {
    label: 'Clipper',
    pathName: 'clipper',
    allow: AbilitySubject.Cast,
    type: LeftMenuType.GENERAL,
    icon: 'clipper',
    hasBreak: true
  }, {
    label: 'Streams',
    pathName: 'streams',
    allow: AbilitySubject.Stream,
    type: LeftMenuType.GENERAL,
    icon: 'streams-square',
    subView: 'input',
    children: [
      { label: 'Input', pathName: 'streams', subView: StreamSubView.Input, type: LeftMenuType.SUB_MENU },
      { label: 'Output', pathName: 'streams', subView: StreamSubView.Output, type: LeftMenuType.SUB_MENU }
    ]
  }, {
    label: 'Team',
    pathName: 'team',
    subView: 'active',
    allow: AbilitySubject.Members,
    type: LeftMenuType.GENERAL,
    icon: 'user-star',
    children: [
      { label: 'Active', pathName: 'team', subView: 'active', type: LeftMenuType.SUB_MENU },
      { label: 'Pending', pathName: 'team', subView: 'pending', type: LeftMenuType.SUB_MENU }
    ]
  }, {
    label: 'Media',
    pathName: 'assets',
    allow: AbilitySubject.AssetGroups,
    type: LeftMenuType.GENERAL,
    icon: 'assets'
  }, {
    label: 'Templates',
    pathName: 'castTemplates',
    allow: AbilitySubject.CastTemplates,
    type: LeftMenuType.GENERAL,
    icon: 'templates'
  }, {
    label: 'Layouts',
    pathName: 'layout',
    allow: AbilitySubject.Layout,
    type: LeftMenuType.GENERAL,
    icon: 'scenes'
  }, {
    label: 'Settings',
    pathName: 'teamsettings',
    subView: 'groups',
    allow: AbilitySubject.TeamSettings,
    type: LeftMenuType.GENERAL,
    icon: 'settings',
    hasBreak: true,
    children: [
      { label: 'Branding', pathName: 'teamsettings', subView: 'branding', type: LeftMenuType.SUB_MENU },
      { label: 'Teams', pathName: 'teamsettings', subView: 'groups', type: LeftMenuType.SUB_MENU },
      { label: 'Roles', pathName: 'teamsettings', subView: 'roles', type: LeftMenuType.SUB_MENU }
    ]
  }, {
    label: 'Billing',
    pathName: 'reports', // hidden except for admin, superadmin
    allow: AbilitySubject.Report,
    type: LeftMenuType.GENERAL,
    icon: 'billing',
    hasBreak: true
  }, {
    label: 'Dashboard',
    pathName: 'dashboard',
    allow: AbilitySubject.Report,
    type: LeftMenuType.GENERAL,
    icon: 'fan-polls',
    hasBreak: true
  },{
    label: 'Moderation',
    pathName: 'moderation',
    allow: AbilitySubject.Cast,
    type: LeftMenuType.GENERAL,
    icon: 'eye'
  }
]

const DASHBOARD_ITEMS_OPS = [
  {
    label: 'Operations',
    pathName: 'ops',
    subView: 'casts',
    icon: 'ops',
    type: LeftMenuType.OPS,
    children: [
      { label: 'Productions', pathName: 'ops', icon: 'events' },
      { label: 'Requests', pathName: 'opsRequest', icon: 'requests' },
      { label: 'Warnings', pathName: 'opsWarnings', icon: 'warning' },
      {
        label: 'Clients',
        pathName: 'teams',
        subView: 'active',
        icon: 'user-star',
        children: [
          { label: 'Active', pathName: 'teams', subView: 'active', icon: '' },
          { label: 'Inactive', pathName: 'teams', subView: 'deleted', icon: '' }
        ]
      },
      { label: 'Streams', pathName: 'opsSrt', icon: 'streams-square' },
      { label: 'Servers', pathName: 'opswrtcservers', icon: 'server-2' },
      { label: 'Stop manager', pathName: 'opsstartstopmanager', icon: 'pause' },
      { label: 'Transcoders', pathName: 'opstranscoding', icon: 'rotate' },
      { label: 'Releases', pathName: 'ops_backendservices', icon: 'rocket' },
      { label: 'Archiver', pathName: 'opsarchiver', icon: 'assets' },
      { label: 'Settings', pathName: 'opsSettings', icon: 'settings' },
      { label: 'Statistics', pathName: 'ops_stats', icon: 'fan-polls' },
      { label: 'Fan rooms', pathName: 'opsRoomsProcessing', icon: 'csv' }
    ]
  }, {
    label: 'Style Guide',
    pathName: 'styleguide',
    icon: 'color-palette',
    type: LeftMenuType.OPS,
    children: [
      { label: 'Themes', pathName: 'styleguide' },
      { label: 'Input', pathName: 'kisweInput' }
    ]
  }
]

const filterAllowedItems = (items: LeftMenuLabel[]): LeftMenuLabel[] => {
  return items.reduce((filteredItems: LeftMenuLabel[], item) => {
    if (!hasFeature(Feature.Dashboard) && item.label === 'Dashboard') return filteredItems
    if (item.children) {
      item.children = item.children.filter((child) => !child.allow || can.value(AbilityAction.Read, child.allow))
    }
    if (!item.allow || can.value(AbilityAction.Read, item.allow)) {
      filteredItems.push({ ...item })
    }
    return filteredItems
  }, [])
}

const useDashboardItems = () => {
  const dashboardItems = computed<LeftMenuLabel[]>(() => filterAllowedItems(DASHBOARD_ITEMS))
  const opsMenuItems = computed(() => DASHBOARD_ITEMS_OPS)

  return {
    dashboardItems,
    opsMenuItems
  }
}

export default useDashboardItems
